import React from 'react';

import { useClassnames, bem } from '@lumapps/classnames';
import { mdiChevronLeft, mdiChevronRight } from '@lumapps/lumx/icons';
import { IconButton, FlexBoxProps } from '@lumapps/lumx/react';
import { GLOBAL, useTranslate } from '@lumapps/translations';

import { useArrowSideScrollContainer, UseArrowSideScrollContainerProps } from '../../hooks/useArrowSideScrollContainer';
import { ArrowSideScrollContainer as UI } from './ArrowSideScrollContainer';

export interface ArrowSideScrollContainerProps extends UseArrowSideScrollContainerProps {
    children: React.ReactNode;
    scrollZoneProps?: FlexBoxProps;
}

export const CLASSNAME = 'lumx-arrow-side-scroll-container';
const { element } = bem(CLASSNAME);

/**
 * A scrollable horizontal FlexBox but with scrollbars replaced by left/right arrow buttons.
 *
 * Warning: you **must** wrap the items in the container with `ArrowSideScrollContainer.Item` and
 *   they **must** support className forwarding. (see stories for example)
 *
 * @family Layouts
 */
export const ArrowSideScrollContainer = ({
    children,
    scrollZoneProps,
    paddingHorizontal,
    paddingVertical,
    ...forwardProps
}: ArrowSideScrollContainerProps) => {
    const { element } = useClassnames(CLASSNAME);
    const { translateKey } = useTranslate();
    const { style, onScrollLeft, onScrollRight, setScrollZone, showArrowRight, showArrowLeft, ref } =
        useArrowSideScrollContainer({ paddingHorizontal, paddingVertical });

    return (
        <UI
            scrollRef={ref}
            {...forwardProps}
            style={style}
            setScrollZone={setScrollZone}
            scrollZoneProps={{
                ...scrollZoneProps,
                // Enable/disable arrow left/right masking gradient
                style: {
                    ...scrollZoneProps?.style,
                    '--_arrow-right-alpha': showArrowRight ? '0' : '1',
                    '--_arrow-left-alpha': showArrowLeft ? '0' : '1',
                },
            }}
            arrowLeft={
                showArrowLeft && (
                    <IconButton
                        className={element('arrow', { left: true })}
                        label={translateKey(GLOBAL.PREVIOUS)}
                        icon={mdiChevronLeft}
                        emphasis="low"
                        onClick={onScrollLeft}
                    />
                )
            }
            arrowRight={
                showArrowRight && (
                    <IconButton
                        className={element('arrow', { right: true })}
                        label={translateKey(GLOBAL.NEXT)}
                        icon={mdiChevronRight}
                        emphasis="low"
                        onClick={onScrollRight}
                    />
                )
            }
        >
            {children}
        </UI>
    );
};

/** Wraps items in the ArrowSideScrollContainer and injects the item className. */
ArrowSideScrollContainer.Item = UI.Item;

export const ARROW_SIDE_SCROLL_CONTAINER_ITEM_CLASSNAME = element('item');
